import React, { useCallback, useMemo } from "react";
import {
  Container,
  TextField,
  Card,
  Box,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import {
  useText,
  useGetData,
  usePutData,
  usePostData,
  useGetList,
} from "../../hooks";
import useStore from "../../context";
import * as Api from "../../service";
import { Save, Select } from "../../components";
import { useParams } from "react-router-dom";
import { useTranslation } from "localization/translation";

const DEALER_CODE_REGEX = /^[A-Za-z]+-[A-Za-z0-9]+$/;

const COUNTRIES_QUERY = {
  sort: "name_en",
  order: "asc",
};

const initForm = {
  code: "",
  name: "",
  country_id: "",
  notes: "",
  is_active: true,
};

const Form = () => {
  const { id } = useParams();
  const [data, , refresh] = useGetData(id && Api.dealerCodes.getById, id);
  const postCallback = usePostData(Api.dealerCodes.create, refresh);
  const updateCallback = usePutData(Api.dealerCodes.update, refresh);
  const { setErrorMsg, lang } = useStore();
  const translation = useTranslation();
  const [countries] = useGetList(Api.countries.getAll, COUNTRIES_QUERY);

  const formData = useMemo(() => {
    if (!data) return initForm;
    return {
      code: data.code || "",
      name: data.name || "",
      country_id: data.country_id || data.country?.id || "",
      notes: data.notes || "",
      is_active: !!data.is_active,
    };
  }, [data]);

  const [text, setText, , input] = useText(formData);
  const countryNameField = `name_${lang || "en"}`;

  const submitForm = useCallback(
    async (e) => {
      e.preventDefault();

      const payload = {
        code: String(text.code || "").trim(),
        name: String(text.name || "").trim(),
        country_id: text.country_id ? Number(text.country_id) : null,
        notes: text.notes ? String(text.notes).trim() : null,
        is_active: !!text.is_active,
      };

      if (!payload.code || !payload.name || !payload.country_id) {
        setErrorMsg(translation.fill_required_fields || "Code, name and country are required");
        return;
      }

      if (!DEALER_CODE_REGEX.test(payload.code)) {
        setErrorMsg(translation.invalid_dealer_code_format);
        return;
      }

      if (id) {
        updateCallback({ ...payload, id: Number(id) });
      } else {
        postCallback(payload);
      }
    },
    [id, postCallback, setErrorMsg, text, translation, updateCallback]
  );

  return (
    <Container>
      <Card className="shadow-xxl px-4 py-2">
        <Box component="form" onSubmit={submitForm}>
          <FormControlLabel
            control={
              <div className="m-2">
                <Switch
                  onClick={() =>
                    setText((st) => ({ ...st, is_active: !st.is_active }))
                  }
                  checked={!!text.is_active}
                  className="switch-medium toggle-switch-first"
                />
              </div>
            }
            label={translation.enable_disable}
          />
          <br />
          <br />

          <TextField
            {...input("code")}
            label={translation.code}
            helperText={translation.dealer_code_format_hint}
          />
          <TextField {...input("name")} label={translation.name} />
          {!!countries?.length && (
            <Select
              onChange={(val) =>
                setText((st) => ({ ...st, country_id: val }))
              }
              value={text.country_id || ""}
              field={countryNameField}
              label={translation.country}
              options={countries}
              noCard
            />
          )}
          <TextField
            {...input("notes")}
            label={translation.notes}
            required={false}
            multiline
            rows={3}
          />
          <Save />
        </Box>
      </Card>
    </Container>
  );
};

export default Form;
